import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { feedItems, siteInfo } from '@/mocks/feed';
import { useSeo } from '@/hooks/useSeo';
import Tag from '@/components/base/Tag';
import HeatMeter from '@/components/base/HeatMeter';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FeedCard from '@/components/feature/FeedCard';
import ArticleTimeline from './components/ArticleTimeline';
import ArticleToc from './components/ArticleToc';

const stageTone = (stage: string): 'primary' | 'accent' | 'secondary' => {
  if (stage === '出现反转') return 'accent';
  if (stage === '热度回落') return 'secondary';
  return 'primary';
};

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const item = useMemo(() => feedItems.find((f) => f.slug === slug), [slug]);

  const related = useMemo(() => {
    if (!item) return [];
    const same = feedItems.filter((f) => f.slug !== item.slug && f.category === item.category);
    const others = feedItems.filter((f) => f.slug !== item.slug && f.category !== item.category);
    return [...same, ...others].slice(0, 3);
  }, [item]);

  const jsonLd = useMemo(() => {
    if (!item) return undefined;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: item.title,
        description: item.summary,
        articleSection: item.category,
        inLanguage: 'zh-CN',
        url: `${siteInfo.domain}/article/${item.slug}`,
        author: { '@type': 'Organization', name: item.author },
        publisher: { '@type': 'Organization', name: siteInfo.name, url: `${siteInfo.domain}/` },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${siteInfo.domain}/` },
          { '@type': 'ListItem', position: 2, name: '瓜田情报库', item: `${siteInfo.domain}/feed` },
          {
            '@type': 'ListItem',
            position: 3,
            name: item.category,
            item: `${siteInfo.domain}/feed?cat=${encodeURIComponent(item.category)}`,
          },
          { '@type': 'ListItem', position: 4, name: item.title, item: `${siteInfo.domain}/article/${item.slug}` },
        ],
      },
    ];
  }, [item]);

  useSeo({
    title: item ? `${item.title}｜夜半瓜社` : '条目不存在｜夜半瓜社',
    description: item
      ? `${item.summary.slice(0, 110)}（${item.category} · ${item.timeSlot}，${item.publishedAt} 整理）`
      : '这条条目可能已经被合并或调整，可以回到瓜田情报库查看最新内容。',
    keywords: item
      ? `${item.category},${item.timeSlot},深夜黑料,午夜话题,${item.title.slice(0, 12)}`
      : '深夜黑料,午夜话题,瓜田情报库',
    path: item ? `/article/${item.slug}` : '/feed',
    ogType: 'article',
    jsonLd,
  });

  if (!item) {
    return (
      <main className="mx-auto flex min-h-[70vh] w-full max-w-3xl flex-col items-center justify-center px-4 pt-24 pb-16 text-center md:px-8">
        <i className="ri-file-unknow-line text-5xl text-foreground-500" />
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这条条目暂时找不到</h1>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600">
          它可能已经被合并进更新的一条，也可能还在核查中。回到情报库看看最新的整理。
        </p>
        <Link
          to="/feed"
          className="mt-6 inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 font-label text-sm text-white transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line" />
          返回瓜田情报库
        </Link>
      </main>
    );
  }

  const needsReview = ['刚起头', '观察中', '出现反转'].includes(item.stage);

  return (
    <main className="mx-auto w-full max-w-[1440px] px-4 pt-24 pb-16 md:px-8 md:pt-28 md:pb-20">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '瓜田情报库', to: '/feed' },
          { label: item.category, to: `/feed?cat=${encodeURIComponent(item.category)}` },
          { label: item.title },
        ]}
      />

      <article className="mt-6">
        <header className="max-w-4xl">
          <div className="flex flex-wrap items-center gap-2">
            <Tag tone={stageTone(item.stage)}>{item.category}</Tag>
            <Tag tone="neutral">{item.timeSlot}</Tag>
            <Tag tone="neutral">阶段：{item.stage}</Tag>
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            {item.title}
          </h1>
          <p className="mt-5 border-l-2 border-primary-500/60 pl-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            {item.summary}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {item.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" />
              {item.publishedAt}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-book-open-line" />
              约 {item.readMinutes} 分钟
            </span>
            <HeatMeter value={item.heat} tag="热" />
          </div>
        </header>

        <figure className="mt-8 overflow-hidden rounded-lg border border-background-200/70">
          <div className="h-[240px] w-full md:h-[420px]">
            <img
              src={item.image}
              alt={`${item.title} - ${item.category}条目配图`}
              title={`${item.title} - 深夜黑料条目配图`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <figcaption className="border-t border-background-200/70 bg-background-100/50 px-4 py-3 text-xs text-foreground-500">
            配图为栏目氛围图，与原事件无直接关联。
          </figcaption>
        </figure>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            {needsReview ? (
              <p className="mb-6 flex items-start gap-2.5 rounded-md border border-accent-500/30 bg-accent-500/10 px-4 py-3 text-sm leading-relaxed text-accent-200">
                <i className="ri-alert-line mt-0.5" />
                本条仍处于「{item.stage}」阶段，材料尚未完成交叉验证，结论可能随时间更新，建议天亮后再看一遍。
              </p>
            ) : null}

            <div className="prose-night text-[15px] text-foreground-700">
              {item.content.map((paragraph, index) => (
                <p key={`paragraph-${index}`} id={`paragraph-${index}`} className="scroll-mt-24">
                  {paragraph}
                </p>
              ))}
            </div>

            <ArticleTimeline events={item.timeline} />

            <div className="mt-8 rounded-lg border border-background-200/70 bg-background-100/40 p-5">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-900">
                <i className="ri-shield-check-line text-accent-400" />
                整理说明
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                本条内容为深夜话题进程整理，所有涉及对象均已做去标识化处理，不指向任何真实个人，也不构成事实认定。
                如果你发现信息有误，欢迎写信到 {siteInfo.email} 说明，我们会重新核对并更新。
              </p>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="flex flex-col gap-6 lg:sticky lg:top-24">
              <ArticleToc paragraphs={item.content} />

              <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">相关栏目</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to={`/feed?cat=${encodeURIComponent(item.category)}`}
                    className="rounded-full border border-primary-500/40 bg-primary-500/10 px-3 py-1.5 text-xs text-primary-200 transition-colors hover:bg-primary-500/20"
                  >
                    {item.category}
                  </Link>
                  <Link
                    to={`/feed?slot=${encodeURIComponent(item.timeSlot)}`}
                    className="rounded-full border border-accent-500/40 bg-accent-500/10 px-3 py-1.5 text-xs text-accent-200 transition-colors hover:bg-accent-500/20"
                  >
                    {item.timeSlot}
                  </Link>
                  <Link
                    to="/feed"
                    className="rounded-full border border-background-200 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:text-foreground-950"
                  >
                    全部条目
                  </Link>
                </div>
              </div>

              <div className="rounded-lg border border-primary-500/30 bg-primary-500/10 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">不想一直盯着？</h2>
                <p className="mt-2 text-xs leading-relaxed text-foreground-700">
                  订阅后每天早上八点收到一封整理邮件，夜里的变动白天再看。
                </p>
                <Link
                  to="/#subscribe"
                  className="mt-4 inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 font-label text-xs text-white transition-colors hover:bg-primary-600"
                >
                  <i className="ri-mail-send-line" />
                  深夜订阅
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      {related.length > 0 ? (
        <section className="mt-16 border-t border-background-200/70 pt-10">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="h-3 w-[3px] rounded-full bg-primary-500" />
                <span className="font-label text-xs tracking-[0.3em] text-primary-300 uppercase">Keep Reading</span>
              </div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">你可能还想跟进</h2>
            </div>
            <Link
              to="/feed"
              className="hidden whitespace-nowrap items-center gap-1.5 font-label text-sm text-foreground-700 transition-colors hover:text-primary-300 sm:inline-flex"
            >
              更多条目
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((rel) => (
              <FeedCard key={rel.slug} item={rel} showHeat />
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}